import { alpha, createTheme } from '@mui/material/styles';
import { BRAND_COLORS, BRAND_FONTS } from './constants/branding';

const { darkPurple, mediumPurple, lightLavender, white, black, canvas } = BRAND_COLORS;

const theme = createTheme({
  palette: {
    primary: {
      main: darkPurple,
      light: mediumPurple,
      dark: '#2f3254',
      contrastText: white,
    },
    secondary: {
      main: mediumPurple,
      light: lightLavender,
      dark: darkPurple,
      contrastText: darkPurple,
    },
    tertiary: {
      main: mediumPurple,
      light: lightLavender,
      dark: darkPurple,
      contrastText: darkPurple,
    },
    background: {
      default: canvas,
      paper: white,
    },
    text: {
      primary: darkPurple,
      secondary: alpha(darkPurple, 0.72),
    },
    success: {
      main: '#2e7d32',
      light: '#4caf50',
      dark: '#1b5e20',
    },
    warning: {
      main: '#ed6c02',
      light: '#ff9800',
      dark: '#e65100',
    },
    error: {
      main: '#c62828',
      light: '#ef5350',
      dark: '#8e0000',
    },
    info: {
      main: darkPurple,
      light: mediumPurple,
      dark: '#2f3254',
    },
    divider: alpha(darkPurple, 0.12),
    grey: {
      50: '#faf8fb',
      100: canvas,
      200: lightLavender,
      300: '#d4c8d8',
      400: mediumPurple,
      500: '#8b84a8',
      600: '#6b6788',
      700: darkPurple,
      800: '#2f3254',
      900: black,
    },
  },
  typography: {
    fontFamily: BRAND_FONTS.ui,
    h1: { fontWeight: 700, fontSize: '2rem', lineHeight: 1.2, color: darkPurple },
    h2: { fontWeight: 600, fontSize: '1.75rem', lineHeight: 1.3, color: darkPurple },
    h3: { fontWeight: 600, fontSize: '1.5rem', lineHeight: 1.3, color: darkPurple },
    h4: { fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.4, color: darkPurple },
    h5: { fontWeight: 500, fontSize: '1.125rem', lineHeight: 1.4, color: darkPurple },
    h6: { fontWeight: 500, fontSize: '1rem', lineHeight: 1.4, color: darkPurple },
    body1: { fontSize: '0.875rem', lineHeight: 1.5 },
    body2: { fontSize: '0.75rem', lineHeight: 1.4 },
    button: { textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: BRAND_FONTS.ui,
          backgroundColor: canvas,
          color: darkPurple,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 10,
          fontWeight: 600,
          padding: '8px 18px',
          minHeight: 36,
        },
        containedPrimary: {
          boxShadow: `0 1px 3px ${alpha(darkPurple, 0.28)}`,
          '&:hover': {
            backgroundColor: '#2f3254',
            boxShadow: `0 2px 8px ${alpha(darkPurple, 0.36)}`,
          },
        },
        outlinedPrimary: {
          borderColor: darkPurple,
          '&:hover': {
            backgroundColor: alpha(lightLavender, 0.45),
            borderColor: darkPurple,
          },
        },
        textPrimary: {
          '&:hover': {
            backgroundColor: alpha(lightLavender, 0.45),
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: `0 1px 4px ${alpha(darkPurple, 0.08)}`,
          border: `1px solid ${alpha(darkPurple, 0.1)}`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${alpha(darkPurple, 0.1)}`,
          boxShadow: `0 1px 4px ${alpha(darkPurple, 0.08)}`,
          '&:hover': {
            boxShadow: `0 4px 14px ${alpha(darkPurple, 0.12)}`,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            backgroundColor: white,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: mediumPurple,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: darkPurple,
              borderWidth: 2,
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: darkPurple,
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: lightLavender,
          '& .MuiTableCell-head': {
            backgroundColor: lightLavender,
            fontWeight: 600,
            fontSize: '0.75rem',
            color: darkPurple,
            borderBottom: `2px solid ${mediumPurple}`,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            padding: '12px 16px',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: alpha(lightLavender, 0.35),
          },
          '&:nth-of-type(even)': {
            backgroundColor: alpha(canvas, 0.8),
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${alpha(darkPurple, 0.1)}`,
          padding: '12px 16px',
          fontSize: '0.875rem',
        },
        stickyHeader: {
          backgroundColor: lightLavender,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          fontSize: '0.75rem',
          height: 24,
        },
        colorPrimary: {
          backgroundColor: alpha(darkPurple, 0.1),
          color: darkPurple,
          border: `1px solid ${alpha(darkPurple, 0.22)}`,
        },
        colorSecondary: {
          backgroundColor: alpha(lightLavender, 0.8),
          color: darkPurple,
          border: `1px solid ${alpha(mediumPurple, 0.6)}`,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: white,
          color: darkPurple,
          borderBottom: `1px solid ${alpha(mediumPurple, 0.35)}`,
          boxShadow: `0 1px 4px ${alpha(darkPurple, 0.08)}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: white,
          borderRight: `1px solid ${alpha(mediumPurple, 0.35)}`,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          margin: '2px 8px',
          minHeight: 40,
          paddingLeft: 16,
          paddingRight: 16,
          '&:hover': {
            backgroundColor: alpha(lightLavender, 0.55),
          },
          '&.Mui-selected': {
            backgroundColor: alpha(lightLavender, 0.85),
            color: darkPurple,
            fontWeight: 600,
            borderLeft: `4px solid ${darkPurple}`,
            '&:hover': {
              backgroundColor: lightLavender,
            },
            '& .MuiListItemIcon-root': {
              color: darkPurple,
            },
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: '0.875rem',
          fontWeight: 500,
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: alpha(darkPurple, 0.55),
          minWidth: 40,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: alpha(mediumPurple, 0.4),
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
        standardInfo: {
          backgroundColor: alpha(lightLavender, 0.7),
          color: darkPurple,
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
});

declare module '@mui/material/styles' {
  interface Palette {
    tertiary: Palette['primary'];
  }
  interface PaletteOptions {
    tertiary: PaletteOptions['primary'];
  }
}

export default theme;
